import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Card } from '../components/Card';
import { Rejection } from '../components/Rejection';
import { PageHeader } from '../components/Shell';
import { Waiting } from '../components/Waiting';
import { getOrganization, type Organization } from '../net/brandProfile';
import { ApiError } from '../net/client';
import {
  payloadIndex,
  registeredText,
  registerSamples,
  resetDemoScans,
  toPayloads,
} from '../net/demoSamples';
import { listAllProducts, type Product } from '../net/products';
import { useSession, useSessionState } from '../session/useSession';
import { colors, radius, shell, type, verdict } from '../theme';
import { qrDetector, readPhotos, readingText, type QrDetector } from './qrPhotos';

/**
 * Q6, demo samples. No board draws it: the user approved this layout on
 * 2026-10-08, D36, and every value is Code batches' own.
 *
 * Register on the left: a product, an optional batch number, and one box of
 * payloads, one per line. Upload list appends a text or CSV file's lines to
 * the box; Upload QR photos appends what the browser's own decoder reads, and
 * where it cannot read QR the button gives way to a note. So everything is
 * seen before it is sent. A refusal keyed to a payload names its line. On the
 * right, Reset demo scans with a confirm step, and the demo scanners staff
 * set. Both routes are admin only; a member sees the scanners and a card. An
 * org not in demo mode sees one card and nothing else.
 */

type Load =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'loaded'; organization: Organization; products: Product[] };

type Problems = { general?: string; product?: string; payloads?: string; batchNumber?: string };

type Reset =
  | { step: 'idle' }
  | { step: 'confirming' }
  | { step: 'resetting' }
  | { step: 'done'; count: number }
  | { step: 'failed'; message: string };

export function DemoSamples() {
  const state = useSessionState();
  const isAdmin = state.status === 'signed_in' && state.user.role === 'org_admin';
  const { api } = useSession();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [chosen, setChosen] = useState<string | null>(null);
  const [batchNumber, setBatchNumber] = useState('');
  const [text, setText] = useState('');
  const [registering, setRegistering] = useState(false);
  const [problems, setProblems] = useState<Problems>({});
  const [result, setResult] = useState<string | null>(null);
  const [detector, setDetector] = useState<QrDetector | null | undefined>(undefined);
  const [reading, setReading] = useState<string | null>(null);
  const [reset, setReset] = useState<Reset>({ step: 'idle' });
  const listInput = useRef<HTMLInputElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    Promise.all([getOrganization(api, signal), listAllProducts(api, signal)]).then(
      ([organization, products]) => setLoad({ status: 'loaded', organization, products }),
      (error: unknown) => {
        if (!signal.aborted) setLoad({ status: 'failed', error });
      },
    );
    return () => controller.abort();
  }, [api]);

  useEffect(() => {
    let live = true;
    void qrDetector().then((found) => {
      if (live) setDetector(found);
    });
    return () => {
      live = false;
    };
  }, []);

  const products = load.status === 'loaded' ? load.products : [];
  const active = products.filter((p) => p.status === 'active');
  const productId = chosen ?? active[0]?.id ?? '';
  const payloads = toPayloads(text);

  const append = (lines: string[]) => {
    if (lines.length === 0) return;
    setText((current) => {
      const kept = current.replace(/\s+$/, '');
      return (kept === '' ? '' : `${kept}\n`) + lines.join('\n') + '\n';
    });
  };

  const uploadList = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    append(toPayloads(await file.text()));
  };

  const uploadPhotos = async (files: FileList | null) => {
    if (!files || files.length === 0 || !detector) return;
    const list = Array.from(files);
    setReading(`Reading ${list.length} photo${list.length === 1 ? '' : 's'}…`);
    const found = await readPhotos(list, detector);
    append(found.payloads);
    setReading(readingText(list.length, found));
  };

  const register = async () => {
    setRegistering(true);
    setProblems({});
    setResult(null);
    const lot = batchNumber.trim();
    try {
      const answer = await registerSamples(api, {
        productId,
        payloads,
        ...(lot !== '' ? { provenance: { batchNumber: lot } } : {}),
      });
      setResult(registeredText(answer));
      setText('');
      setBatchNumber('');
      setReading(null);
    } catch (error) {
      setProblems(fromFields(error, payloads));
    }
    setRegistering(false);
  };

  const runReset = async () => {
    setReset({ step: 'resetting' });
    try {
      setReset({ step: 'done', count: await resetDemoScans(api) });
    } catch (error) {
      setReset({ step: 'failed', message: failureText(error) });
    }
  };

  const header = (
    <PageHeader
      title="Demo samples"
      subtitle="Other brands' QR codes, registered for your demo scanners to scan in the app"
    />
  );

  if (load.status === 'failed') {
    return (
      <>
        {header}
        <Card title="The demo samples could not be loaded" body={failureText(load.error)} />
      </>
    );
  }

  if (load.status === 'loaded' && load.organization.demoMode !== true) {
    return (
      <>
        {header}
        <Card
          title="This organization is not in demo mode"
          body="Demo samples are for demonstration organizations only. Veriplus staff turn demo mode on."
        />
      </>
    );
  }

  const scanners = load.status === 'loaded' ? (load.organization.demoScannerEmails ?? []) : [];

  return (
    <>
      {load.status === 'loading' ? <Waiting>Loading demo samples</Waiting> : null}
      {header}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '400px 1fr',
          gap: 16,
          flexGrow: 1,
          alignItems: 'start',
        }}
      >
        <div style={column}>
          {isAdmin ? (
            <form
              aria-label="Register samples"
              onSubmit={(e) => {
                e.preventDefault();
                void register();
              }}
              style={cardBox}
            >
              <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <h2 style={{ ...type.panelTitle, color: colors.navy, margin: 0 }}>
                  Register samples
                </h2>
                {problems.general !== undefined ? <Rejection>{problems.general}</Rejection> : null}
                <Field id="demo-product" label="Product" message={problems.product}>
                  <div style={{ position: 'relative' }}>
                    <select
                      id="demo-product"
                      value={productId}
                      onChange={(e) => setChosen(e.target.value)}
                      aria-invalid={problems.product !== undefined}
                      style={{ ...input, appearance: 'none', paddingRight: 36, cursor: 'pointer' }}
                    >
                      {active.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                      style={{ position: 'absolute', right: 14, top: 15, pointerEvents: 'none' }}
                    >
                      <path
                        d="M6 9 L12 15 L18 9"
                        stroke={colors.mutedInk}
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </Field>
                <Field id="demo-batch" label="Batch / lot no." message={problems.batchNumber}>
                  <input
                    id="demo-batch"
                    value={batchNumber}
                    onChange={(e) => setBatchNumber(e.target.value)}
                    aria-invalid={problems.batchNumber !== undefined}
                    style={input}
                  />
                </Field>
                <Field
                  id="demo-payloads"
                  label="Payloads, one per line"
                  message={problems.payloads}
                >
                  <textarea
                    id="demo-payloads"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    aria-invalid={problems.payloads !== undefined}
                    spellCheck={false}
                    style={textarea}
                  />
                  <div style={note}>
                    {payloads.length.toLocaleString('en-US')} line
                    {payloads.length === 1 ? '' : 's'}
                  </div>
                </Field>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    className="vp-outline"
                    type="button"
                    onClick={() => listInput.current?.click()}
                    style={outlineButton}
                  >
                    Upload list
                  </button>
                  <input
                    ref={listInput}
                    type="file"
                    accept=".txt,.csv,text/plain,text/csv"
                    aria-label="List file"
                    hidden
                    onChange={(e) => {
                      void uploadList(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  {detector ? (
                    <>
                      <button
                        className="vp-outline"
                        type="button"
                        onClick={() => photoInput.current?.click()}
                        style={outlineButton}
                      >
                        Upload QR photos
                      </button>
                      <input
                        ref={photoInput}
                        type="file"
                        accept="image/*"
                        multiple
                        aria-label="QR photos"
                        hidden
                        onChange={(e) => {
                          void uploadPhotos(e.target.files);
                          e.target.value = '';
                        }}
                      />
                    </>
                  ) : null}
                </div>
                {detector === null ? (
                  <div style={note}>
                    This browser cannot read QR codes from photos. Chrome or Edge on a Mac can, or
                    paste the payloads.
                  </div>
                ) : null}
                {reading !== null ? (
                  <div role="status" style={{ ...type.metaBold, color: colors.mutedInk }}>
                    {reading}
                  </div>
                ) : null}
                <button
                  className="vp-fill"
                  type="submit"
                  disabled={
                    registering ||
                    load.status !== 'loaded' ||
                    productId === '' ||
                    payloads.length === 0
                  }
                  aria-busy={registering}
                  style={fillButton}
                >
                  Register samples
                </button>
                {result !== null ? (
                  <div role="status" style={{ ...type.metaBold, color: verdict.genuine.fg }}>
                    {result}
                  </div>
                ) : null}
              </div>
            </form>
          ) : (
            <Card
              title="Registering samples is for admins"
              body="Only organization admins can register demo samples or reset their scans."
            />
          )}
        </div>

        <div style={column}>
          {isAdmin ? (
            <section aria-label="Reset demo scans" style={cardBox}>
              <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <h2 style={{ ...type.cardTitle, color: colors.navy, margin: 0 }}>
                  Reset demo scans
                </h2>
                <div style={{ ...type.meta, color: colors.mutedInk, lineHeight: 1.5 }}>
                  A sample&apos;s first scan reads Genuine, and later scans follow the first-scan
                  rule. Reset makes every demo sample unscanned again. Scan history stays, and
                  issued codes are never touched.
                </div>
                {reset.step === 'confirming' ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ ...type.bodyStrong, color: colors.navy }}>
                      Reset every demo sample?
                    </span>
                    <button
                      className="vp-fill"
                      type="button"
                      onClick={() => void runReset()}
                      style={fillButton}
                    >
                      Reset
                    </button>
                    <button
                      className="vp-outline"
                      type="button"
                      onClick={() => setReset({ step: 'idle' })}
                      style={outlineButton}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div>
                    <button
                      className="vp-outline"
                      type="button"
                      disabled={reset.step === 'resetting' || load.status !== 'loaded'}
                      aria-busy={reset.step === 'resetting'}
                      onClick={() => setReset({ step: 'confirming' })}
                      style={outlineButton}
                    >
                      Reset demo scans
                    </button>
                  </div>
                )}
                {reset.step === 'done' ? (
                  <div role="status" style={{ ...type.metaBold, color: verdict.genuine.fg }}>
                    {reset.count.toLocaleString('en-US')} sample{reset.count === 1 ? '' : 's'}{' '}
                    reset.
                  </div>
                ) : null}
                {reset.step === 'failed' ? <Rejection>{reset.message}</Rejection> : null}
              </div>
            </section>
          ) : null}
          <section aria-label="Demo scanners" style={cardBox}>
            <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <h2 style={{ ...type.cardTitle, color: colors.navy, margin: 0 }}>Demo scanners</h2>
              {scanners.length > 0 ? (
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {scanners.map((email) => (
                    <li key={email} style={{ ...type.bodyStrong, color: colors.navy }}>
                      {email}
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ ...type.body, color: colors.mutedInk }}>None yet.</div>
              )}
              <div style={note}>
                Only these accounts see demo samples when they scan. Veriplus staff set them.
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function Field({
  id,
  label,
  message,
  children,
}: {
  id: string;
  label: string;
  message: string | undefined;
  children: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <label htmlFor={id} style={{ ...type.statLabel, color: colors.mutedInk }}>
        {label}
      </label>
      {children}
      {message !== undefined ? (
        <div role="alert" style={{ ...type.caption, color: verdict.unknown.fg }}>
          {message}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Where a register failure lands, D10's rule as Code batches keeps it. A
 * payload's refusal names its line, `body.payloads.3` being line 4 of the
 * lines sent, with the payload itself so it can be found.
 */
function fromFields(error: unknown, sent: string[]): Problems {
  if (!(error instanceof ApiError) || error.details === undefined) {
    return { general: failureText(error) };
  }
  const problems: Problems = {};
  const lines: string[] = [];
  const rest: string[] = [];
  for (const [path, message] of Object.entries(error.details)) {
    const field = path.replace(/^body\./, '');
    const at = payloadIndex(field);
    if (at !== null) {
      const payload = sent[at];
      lines.push(
        `Line ${at + 1}${payload !== undefined ? `, ${shorten(payload)}` : ''}: ${message}`,
      );
    } else if (field === 'payloads') problems.payloads = message;
    else if (field === 'productId') problems.product = message;
    else if (field === 'provenance.batchNumber') problems.batchNumber = message;
    else rest.push(message);
  }
  if (lines.length > 0) problems.payloads = lines.join(' ');
  if (rest.length > 0) problems.general = rest.join(' ');
  return problems;
}

const shorten = (payload: string) => (payload.length > 40 ? `${payload.slice(0, 40)}…` : payload);

function failureText(error: unknown): string {
  if (error instanceof ApiError && error.kind === 'network') {
    return 'The API could not be reached. Reload the page to try again.';
  }
  if (error instanceof Error) return error.message;
  return 'Reload the page to try again.';
}

const column: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 };

const cardBox: CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: radius.card,
};

const note: CSSProperties = { ...type.caption, color: colors.mutedInk, lineHeight: 1.5 };

const input: CSSProperties = {
  height: 44,
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 12,
  background: colors.card,
  border: `1px solid ${colors.line}`,
  padding: '0 14px',
  ...type.body,
  color: colors.navy,
};

/** The product form's description box, taller, in ink rather than muted: these are the data. */
const textarea: CSSProperties = {
  height: 180,
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 12,
  background: colors.card,
  border: `1px solid ${colors.line}`,
  padding: '12px 14px',
  ...type.body,
  color: colors.navy,
  lineHeight: 1.5,
  resize: 'vertical',
  whiteSpace: 'pre',
  overflowX: 'auto',
};

const actionBase: CSSProperties = {
  height: shell.actionButton.height,
  padding: `0 ${shell.actionButton.paddingX}px`,
  borderRadius: shell.actionButton.radius,
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...type.button,
};

const fillButton: CSSProperties = {
  ...actionBase,
  background: colors.brand,
  color: colors.white,
  border: 'none',
};

const outlineButton: CSSProperties = {
  ...actionBase,
  background: colors.card,
  color: colors.navy,
  border: `2px solid ${colors.navy}`,
};
